import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import { createPinia } from 'pinia'
import ElementPlus from 'element-plus'
import { ElMessage } from 'element-plus'
import 'element-plus/dist/index.css'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import {
  enforceBuildLoginRefresh,
  enforceReLoginAfterBrowserClose,
  IDLE_TIMEOUT_MS,
  markUserActive,
  setupMobileSessionWatch,
  shouldLogoutForIdle
} from './utils/auth'
import {
  clearChunkReloadAttemptFlag,
  isChunkLoadError,
  tryReloadOnceOnChunkError
} from './utils/chunkLoadRecovery'
import { installClosableElMessage } from './utils/elMessageDefaults'
import { setupPwaInstallCapture } from './utils/pwaInstall'

installClosableElMessage()
// 尽早监听安装事件，避免组件挂载前错过 beforeinstallprompt
setupPwaInstallCapture()

// 创建应用实例
const app = createApp(App)
enforceBuildLoginRefresh()
enforceReLoginAfterBrowserClose()
setupMobileSessionWatch()

// 创建 Pinia 实例
const pinia = createPinia()

// 注册所有Element Plus图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

// 使用插件（Pinia 必须在 router 之前，否则 /m 重定向里 useUserStore 可能异常导致白屏）
app.use(pinia)
app.use(store) // Vuex store (保留兼容性)
app.use(router)
app.use(ElementPlus)

// 抑制 ResizeObserver 循环警告（这是浏览器的一个已知问题，不影响功能）
const originalError = console.error
const originalWarn = console.warn

// 检查是否是 ResizeObserver 相关错误
const isResizeObserverError = (message) => {
  if (!message || typeof message !== 'string') return false
  return message.includes('ResizeObserver') || 
         message.includes('ResizeObserver loop') ||
         message.includes('ResizeObserver loop completed')
}

console.error = (...args) => {
  const firstArg = args[0]
  if (isResizeObserverError(firstArg) || 
      (typeof firstArg === 'string' && isResizeObserverError(firstArg))) {
    return // 忽略 ResizeObserver 循环警告
  }
  originalError.apply(console, args)
}

console.warn = (...args) => {
  const firstArg = args[0]
  if (isResizeObserverError(firstArg) || 
      (typeof firstArg === 'string' && isResizeObserverError(firstArg))) {
    return // 忽略 ResizeObserver 循环警告
  }
  originalWarn.apply(console, args)
}

// 全局错误事件监听
window.addEventListener('error', (event) => {
  const errorMessage = event.message || (event.error && event.error.message) || ''
  const errorName = event.error?.name || ''
  const errorString = event.error?.toString() || ''

  if (isChunkLoadError(event.error || { message: errorMessage, name: errorName })) {
    event.preventDefault()
    tryReloadOnceOnChunkError((text) => ElMessage.info(text))
    return false
  }
  
  if (isResizeObserverError(errorMessage) || 
      isResizeObserverError(errorName) ||
      isResizeObserverError(errorString) ||
      (event.filename && event.filename.includes('material-inventory'))) {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    return false
  }
}, true) // 使用捕获阶段

// 全局错误处理
app.config.errorHandler = (err, instance, info) => {
  // 忽略 ResizeObserver 循环警告
  if (err) {
    const errMessage = err.message || ''
    const errString = err.toString() || ''
    const errName = err.name || ''
    
    if (isResizeObserverError(errMessage) ||
        isResizeObserverError(errString) ||
        isResizeObserverError(errName) ||
        (typeof err === 'string' && isResizeObserverError(err)) ||
        (info && typeof info === 'string' && isResizeObserverError(info))) {
      return
    }

    if (isChunkLoadError(err)) {
      tryReloadOnceOnChunkError((text) => ElMessage.info(text))
      return
    }
  }
  
  console.error('Vue错误:', err, info)
  // 提取错误消息
  const errorMessage = err?.message || err?.toString() || '发生未知错误'
  if (typeof errorMessage === 'string' && errorMessage !== '[object Object]') {
    // 只在开发环境显示详细错误
    if (process.env.NODE_ENV === 'development') {
      console.error('错误详情:', { err, info })
    }
  }
}

// 捕获未处理的 Promise 拒绝
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason

  // Webpack/Vue CLI：发版后旧 HTML/主包仍引用已删除的异步 chunk 时会抛 ChunkLoadError，自动刷新一次通常可恢复
  if (isChunkLoadError(reason)) {
    event.preventDefault()
    tryReloadOnceOnChunkError((text) => {
      if (text.includes('强制刷新')) ElMessage.error(text)
      else ElMessage.info(text)
    })
    return
  }

  // 检查是否是 ResizeObserver 相关错误
  if (reason) {
    const reasonMessage = reason.message || reason.toString() || ''
    const reasonName = reason.name || ''
    if (isResizeObserverError(reasonMessage) || 
        isResizeObserverError(reasonName) ||
        (typeof reason === 'string' && isResizeObserverError(reason))) {
      event.preventDefault()
      return
    }
  }
  
  // 检查是否是 Element Plus 表单验证错误格式
  // 格式: { 字段名: [Error对象] }
  // 如果是验证错误，说明应该已经在组件中处理了，这里静默处理
  const isValidationError = reason && 
    typeof reason === 'object' && 
    !reason.response && 
    !(reason instanceof Error) &&
    !reason.message &&
    Object.keys(reason).some(key => {
      const value = reason[key]
      return Array.isArray(value) && value.length > 0 && (
        value[0] instanceof Error || 
        (value[0] && typeof value[0] === 'object' && value[0].message)
      )
    })
  
  if (isValidationError) {
    // 表单验证错误，已经在组件中处理，这里静默处理
    // 阻止默认的错误输出，不显示给用户
    event.preventDefault()
    return
  }
  
  console.error('未处理的 Promise 拒绝:', event.reason)
  // 阻止默认的错误输出
  event.preventDefault()
  
  // 提取错误消息
  let errorMessage = '操作失败，请稍后重试'
  
  try {
    // 检查是否是 Element Plus 表单验证错误格式 (字段名: 错误数组)
    // 这种格式通常是对象，有字段名作为键，错误数组作为值
    if (reason && typeof reason === 'object' && !reason.response && !(reason instanceof Error)) {
      // 尝试提取表单验证错误消息
      const validationErrors = []
      let hasValidationErrors = false
      
      for (const fieldName in reason) {
        if (Object.prototype.hasOwnProperty.call(reason, fieldName)) {
          const fieldErrors = reason[fieldName]
          if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            hasValidationErrors = true
            // 提取数组中的错误消息
            fieldErrors.forEach(err => {
              // 优先检查 Error 对象
              if (err instanceof Error) {
                validationErrors.push(err.message || err.toString())
              } else if (err?.message) {
                validationErrors.push(err.message)
              } else if (typeof err === 'string') {
                validationErrors.push(err)
              }
            })
          } else if (fieldErrors?.message) {
            hasValidationErrors = true
            validationErrors.push(fieldErrors.message)
          }
        }
      }
      
      if (hasValidationErrors && validationErrors.length > 0) {
        // 使用第一个验证错误消息，或者合并所有错误
        errorMessage = validationErrors.length === 1 
          ? validationErrors[0] 
          : validationErrors.join('; ')
      }
    }
    
    // 如果还没有提取到错误消息，尝试其他方法
    if (errorMessage === '操作失败，请稍后重试') {
      if (reason?.response?.data?.message) {
        // API 响应错误
        errorMessage = reason.response.data.message
      } else if (reason?.message) {
        // 标准 Error 对象
        errorMessage = reason.message
      } else if (typeof reason === 'string') {
        // 字符串类型的错误
        errorMessage = reason
      } else if (reason?.toString && typeof reason.toString === 'function') {
        // 尝试使用 toString 方法
        const str = reason.toString()
        if (str !== '[object Object]') {
          errorMessage = str
        } else {
          // toString 返回 [object Object]，尝试使用 JSON.stringify
          try {
            const jsonStr = JSON.stringify(reason, null, 2)
            // 如果 JSON 字符串太长，截断它
            errorMessage = jsonStr.length > 200 ? jsonStr.substring(0, 200) + '...' : jsonStr
          } catch (jsonErr) {
            // JSON.stringify 失败，使用默认消息
            errorMessage = '操作失败，请稍后重试'
          }
        }
      }
    }
    
    // 确保错误消息是字符串类型且不是 [object Object]
    if (typeof errorMessage === 'string' && errorMessage !== '[object Object]' && errorMessage !== '操作失败，请稍后重试') {
      // 对于表单验证错误，使用 warning 级别，因为已经在 submitForm 中处理过了
      // 这里只是作为兜底，防止未捕获的情况
      ElMessage.warning(errorMessage)
    } else if (errorMessage !== '操作失败，请稍后重试') {
      // 如果仍然无法获取有效消息，只记录到控制台，不显示给用户
      console.error('无法提取有效的错误消息，原始错误:', reason)
    }
    // 如果 errorMessage 仍然是默认值，说明这不是我们处理的错误类型，静默处理
  } catch (err) {
    // 如果错误处理过程中出错，只记录到控制台
    console.error('处理未捕获的 Promise 拒绝时出错:', err)
  }
})

const activityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart']
activityEvents.forEach((eventName) => {
  window.addEventListener(eventName, markUserActive, { passive: true })
})
markUserActive()

window.setInterval(async () => {
  if (!shouldLogoutForIdle()) return
  const { forceAuthRelogin } = await import('./utils/auth')
  await forceAuthRelogin(
    `超过 ${Math.round(IDLE_TIMEOUT_MS / 60000)} 分钟未操作，请重新登录`
  )
}, 60 * 1000)

// 挂载应用
app.mount('#app')
clearChunkReloadAttemptFlag()

// 注销旧 Service Worker（曾导致主屏幕打开白屏）；暂不注册新 SW，安装桌面仅靠 manifest
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => Promise.all(regs.map((r) => r.unregister())))
    .catch(() => {})
  if (typeof caches !== 'undefined') {
    caches
      .keys()
      .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      .catch(() => {})
  }
}